import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import staticAssetsList from '../../../assets.json';
import { environment } from '../../../environments/environment';
import { SiteConfigInterface } from '../models/config/site-config.interface';

@Injectable({
  providedIn: 'root',
})
export class SiteConfigService {
  private _updateSiteConfig: BehaviorSubject<SiteConfigInterface | null> =
    new BehaviorSubject<SiteConfigInterface | null>(null);
  public readonly updateSiteConfig: Observable<SiteConfigInterface | null> = this._updateSiteConfig.asObservable();
  siteMachineName!: string;
  private staticAssets: { [key: string]: string } = staticAssetsList;
  urlBase = '';
  skinUrlBase = '';
  constructor(
    private router: Router,
    private httpClient: HttpClient,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.skinUrlBase = environment.cdnSkinEndpoint;
    if (environment.production) {
      this.urlBase = environment.cdnEndpoint;
    } else {
      this.urlBase = '';
    }
  }

  applyConfig(
    baseConfig: any,
    siteConfig: any,
    skinConfig?: any,
    useDynamicSkins?: boolean,
    skinNumber?: string,
  ): void {
    siteConfig.ngConfig = {
      ...baseConfig.ngConfig,
      ...siteConfig.ngConfig,
    };
    siteConfig.ngConfig.skinName = '';
    if (skinConfig) {
      siteConfig.sassConfig = {
        ...siteConfig.sassConfig,
        ...skinConfig.sassConfig,
      };
      siteConfig.sassConfigDark = {
        ...siteConfig.sassConfigDark,
        ...skinConfig.sassConfigDark,
      };
      siteConfig.ngConfig.skinName = skinConfig.name;
      if (skinConfig.ngConfig.headerImageDarkModeUrl) {
        siteConfig.ngConfig.headerImageDarkModeUrl = skinConfig.ngConfig.headerImageDarkModeUrl;
      }
    }

    const mergedSiteConfig: SiteConfigInterface = siteConfig;
    if (skinConfig) {
      const skinBase =
        useDynamicSkins === true
          ? this.skinUrlBase + '/site-skins/' + skinNumber + '/'
          : this.urlBase + '/assets/skins/' + skinConfig.name + '/';

      const headerImageUrl = skinConfig.ngConfig.headerImageUrl;
      const headerImageDarkModeUrl = skinConfig.ngConfig.headerImageDarkModeUrl;

      if (headerImageUrl && headerImageDarkModeUrl) {
        mergedSiteConfig.ngConfig.headerImageUrl = this.getCacheBustUrl(
          skinBase + headerImageUrl,
          skinConfig.name,
          'skins',
        );
        mergedSiteConfig.ngConfig.headerImageDarkModeUrl = this.getCacheBustUrl(
          skinBase + headerImageDarkModeUrl,
          skinConfig.name,
          'skins',
        );
      } else if (headerImageUrl) {
        const url = this.getCacheBustUrl(skinBase + headerImageUrl, skinConfig.name, 'skins');
        mergedSiteConfig.ngConfig.headerImageUrl = url;
        mergedSiteConfig.ngConfig.headerImageDarkModeUrl = url;
      } else if (headerImageDarkModeUrl) {
        const url = this.getCacheBustUrl(skinBase + headerImageDarkModeUrl, skinConfig.name, 'skins');
        mergedSiteConfig.ngConfig.headerImageUrl = url;
        mergedSiteConfig.ngConfig.headerImageDarkModeUrl = url;
      } else {
        mergedSiteConfig.ngConfig.headerImageUrl = '';
        mergedSiteConfig.ngConfig.headerImageDarkModeUrl = '';
      }

      const sideMenuImageUrl = skinConfig.ngConfig.sideMenuImageUrl;
      const sideMenuImageDarkModeUrl = skinConfig.ngConfig.sideMenuImageDarkModeUrl;

      if (sideMenuImageUrl && sideMenuImageDarkModeUrl) {
        mergedSiteConfig.ngConfig.sideMenuImageUrl = this.getCacheBustUrl(
          skinBase + sideMenuImageUrl,
          skinConfig.name,
          'skins',
        );
        mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl = this.getCacheBustUrl(
          skinBase + sideMenuImageDarkModeUrl,
          skinConfig.name,
          'skins',
        );
      } else if (sideMenuImageUrl) {
        const url = this.getCacheBustUrl(skinBase + sideMenuImageUrl, skinConfig.name, 'skins');
        mergedSiteConfig.ngConfig.sideMenuImageUrl = url;
        mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl = url;
      } else if (sideMenuImageDarkModeUrl) {
        const url = this.getCacheBustUrl(skinBase + sideMenuImageDarkModeUrl, skinConfig.name, 'skins');
        mergedSiteConfig.ngConfig.sideMenuImageUrl = url;
        mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl = url;
      } else {
        mergedSiteConfig.ngConfig.sideMenuImageUrl = this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            mergedSiteConfig.ngConfig.sideMenuImageUrl,
          skinConfig.name,
          'skins',
        );

        if (mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl === undefined) {
          mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl = mergedSiteConfig.ngConfig.sideMenuImageUrl;
        } else {
          mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl = this.getCacheBustUrl(
            this.urlBase +
              '/assets/sites/' +
              mergedSiteConfig.ngConfig.machineName +
              mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl,
            skinConfig.name,
            'skins',
          );
        }
      }
    } else {
      mergedSiteConfig.ngConfig.headerImageUrl = this.getCacheBustUrl(
        this.urlBase +
          '/assets/sites/' +
          mergedSiteConfig.ngConfig.machineName +
          mergedSiteConfig.ngConfig.headerImageUrl,
        mergedSiteConfig.ngConfig.machineName,
      );
      if (mergedSiteConfig.ngConfig.headerImageDarkModeUrl === undefined) {
        mergedSiteConfig.ngConfig.headerImageDarkModeUrl = mergedSiteConfig.ngConfig.headerImageUrl;
      } else {
        mergedSiteConfig.ngConfig.headerImageDarkModeUrl = this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            mergedSiteConfig.ngConfig.headerImageDarkModeUrl,
          mergedSiteConfig.ngConfig.machineName,
        );
      }

      mergedSiteConfig.ngConfig.sideMenuImageUrl = this.getCacheBustUrl(
        this.urlBase +
          '/assets/sites/' +
          mergedSiteConfig.ngConfig.machineName +
          mergedSiteConfig.ngConfig.sideMenuImageUrl,
        mergedSiteConfig.ngConfig.machineName,
      );

      if (mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl === undefined) {
        mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl = mergedSiteConfig.ngConfig.sideMenuImageUrl;
      } else {
        mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl = this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            mergedSiteConfig.ngConfig.sideMenuImageDarkModeUrl,
          mergedSiteConfig.ngConfig.machineName,
        );
      }
    }

    mergedSiteConfig.ngConfig.footerImageUrl = this.getCacheBustUrl(
      this.urlBase +
        '/assets/sites/' +
        mergedSiteConfig.ngConfig.machineName +
        mergedSiteConfig.ngConfig.footerImageUrl,
      mergedSiteConfig.ngConfig.machineName,
    );
    mergedSiteConfig.ngConfig.loadingImageUrl = this.getCacheBustUrl(
      this.urlBase +
        '/assets/sites/' +
        mergedSiteConfig.ngConfig.machineName +
        mergedSiteConfig.ngConfig.loadingImageUrl,
      mergedSiteConfig.ngConfig.machineName,
    );
    if (mergedSiteConfig.ngConfig.footerImageDarkModeUrl === undefined) {
      mergedSiteConfig.ngConfig.footerImageDarkModeUrl = mergedSiteConfig.ngConfig.footerImageUrl;
    } else {
      mergedSiteConfig.ngConfig.footerImageDarkModeUrl = this.getCacheBustUrl(
        this.urlBase +
          '/assets/sites/' +
          mergedSiteConfig.ngConfig.machineName +
          mergedSiteConfig.ngConfig.footerImageDarkModeUrl,
        mergedSiteConfig.ngConfig.machineName,
      );
    }

    mergedSiteConfig.ngConfig.primarySite =
      mergedSiteConfig.ngConfig.machineName === mergedSiteConfig.ngConfig.primaryMachineName;
    // Set colors
    this.document.documentElement.style.setProperty(
      '--primary-site-color',
      mergedSiteConfig.sassConfig.primarySiteColor,
    );
    this.document.documentElement.style.setProperty(
      '--primary-site-color-dark',
      mergedSiteConfig.sassConfig.primarySiteColorDark,
    );
    this.document.documentElement.style.setProperty(
      '--primary-site-color-dark-text',
      mergedSiteConfig.sassConfig.primarySiteColorDarkText,
    );
    this.document.documentElement.style.setProperty(
      '--primary-site-color-light',
      mergedSiteConfig.sassConfig.primarySiteColorLight,
    );
    this.document.documentElement.style.setProperty(
      '--primary-site-color-light-text',
      mergedSiteConfig.sassConfig.primarySiteColorLightText,
    );
    this.document.documentElement.style.setProperty(
      '--primary-site-color-text',
      mergedSiteConfig.sassConfig.primarySiteColorText,
    );
    this.document.documentElement.style.setProperty(
      '--secondary-site-color',
      mergedSiteConfig.sassConfig.secondarySiteColor,
    );
    this.document.documentElement.style.setProperty(
      '--secondary-site-color-dark',
      mergedSiteConfig.sassConfig.secondarySiteColorDark,
    );
    this.document.documentElement.style.setProperty(
      '--secondary-site-color-dark-text',
      mergedSiteConfig.sassConfig.secondarySiteColorDarkText,
    );
    this.document.documentElement.style.setProperty(
      '--secondary-site-color-light',
      mergedSiteConfig.sassConfig.secondarySiteColorLight,
    );
    this.document.documentElement.style.setProperty(
      '--secondary-site-color-light-text',
      mergedSiteConfig.sassConfig.secondarySiteColorLightText,
    );
    this.document.documentElement.style.setProperty(
      '--secondary-site-color-text',
      mergedSiteConfig.sassConfig.secondarySiteColorText,
    );
    this.document.documentElement.style.setProperty(
      '--page-title-color',
      mergedSiteConfig.sassConfig.headerBackgroundColor,
    );
    this.document.documentElement.style.setProperty(
      '--page-title-color-text',
      mergedSiteConfig.sassConfig.headerTextColor,
    );
    this.document.documentElement.style.setProperty(
      '--accordion-button-color',
      mergedSiteConfig.sassConfig.accordionButtonColor ?? mergedSiteConfig.sassConfig.secondarySiteColor,
    );
    this.document.documentElement.style.setProperty(
      '--accordion-button-color-text',
      mergedSiteConfig.sassConfig.accordionButtonColorText ?? mergedSiteConfig.sassConfig.secondarySiteColorText,
    );

    this.document.documentElement.style.setProperty(
      '--dark-mode-primary-site-color',
      mergedSiteConfig.sassConfigDark?.primarySiteColor ?? mergedSiteConfig.sassConfig.primarySiteColor,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-first-side-menu-hover-color',
      mergedSiteConfig.sassConfigDark?.primarySiteColor ?? mergedSiteConfig.sassConfig.primarySiteColor,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-primary-site-color-dark',
      mergedSiteConfig.sassConfigDark?.primarySiteColorDark ?? mergedSiteConfig.sassConfig.primarySiteColorDark,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-primary-site-color-dark-text',
      mergedSiteConfig.sassConfigDark?.primarySiteColorDarkText ?? mergedSiteConfig.sassConfig.primarySiteColorDarkText,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-primary-site-color-light',
      mergedSiteConfig.sassConfigDark?.primarySiteColorLight ?? mergedSiteConfig.sassConfig.primarySiteColorLight,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-primary-site-color-light-text',
      mergedSiteConfig.sassConfigDark?.primarySiteColorLightText ??
        mergedSiteConfig.sassConfig.primarySiteColorLightText,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-primary-site-color-text',
      mergedSiteConfig.sassConfigDark?.primarySiteColorText ?? mergedSiteConfig.sassConfig.primarySiteColorText,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-secondary-site-color',
      mergedSiteConfig.sassConfigDark?.secondarySiteColor ?? mergedSiteConfig.sassConfig.secondarySiteColor,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-secondary-site-color-dark',
      mergedSiteConfig.sassConfigDark?.secondarySiteColorDark ?? mergedSiteConfig.sassConfig.secondarySiteColorDark,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-secondary-site-color-dark-text',
      mergedSiteConfig.sassConfigDark?.secondarySiteColorDarkText ??
        mergedSiteConfig.sassConfig.secondarySiteColorDarkText,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-secondary-site-color-light',
      mergedSiteConfig.sassConfigDark?.secondarySiteColorLight ?? mergedSiteConfig.sassConfig.secondarySiteColorLight,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-secondary-site-color-light-text',
      mergedSiteConfig.sassConfigDark?.secondarySiteColorLightText ??
        mergedSiteConfig.sassConfig.secondarySiteColorLightText,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-secondary-site-color-text',
      mergedSiteConfig.sassConfigDark?.secondarySiteColorText ?? mergedSiteConfig.sassConfig.secondarySiteColorText,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-page-title-color',
      mergedSiteConfig.sassConfigDark?.headerBackgroundColor ?? mergedSiteConfig.sassConfig.headerBackgroundColor,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-page-title-color-text',
      mergedSiteConfig.sassConfigDark?.headerTextColor ?? mergedSiteConfig.sassConfig.headerTextColor,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-accordion-button-color',
      mergedSiteConfig.sassConfigDark?.accordionButtonColor ?? mergedSiteConfig.sassConfig.secondarySiteColor,
    );
    this.document.documentElement.style.setProperty(
      '--dark-mode-accordion-button-color-text',
      mergedSiteConfig.sassConfigDark?.accordionButtonColorText ?? mergedSiteConfig.sassConfig.secondarySiteColorText,
    );

    // Set favicons
    this.document
      .getElementById('apple57')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-57x57.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple60')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-60x60.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple72')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-72x72.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple76')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-76x76.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple114')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-114x114.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple120')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-120x120.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple144')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-144x144.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple152')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-152x152.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('apple180')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/apple-icon-180x180.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('favicon32')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/favicon-32x32.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('favicon192')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/android-icon-192x192.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('favicon96')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/favicon-96x96.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('favicon16')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/favicon-16x16.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('manifest')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase + '/assets/sites/' + mergedSiteConfig.ngConfig.machineName + '/images/favicons/manifest.json',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('safari')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/safari-pinned-tab.svg',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this.document
      .getElementById('ms-tile-image')
      ?.setAttribute(
        'href',
        this.getCacheBustUrl(
          this.urlBase +
            '/assets/sites/' +
            mergedSiteConfig.ngConfig.machineName +
            '/images/favicons/mstile-144x144.png',
          mergedSiteConfig.ngConfig.machineName,
        ),
      );
    this._updateSiteConfig.next(mergedSiteConfig);
    this.document.getElementById('loading-screen')?.style.setProperty('display', 'none');
  }

  public async setConfig(machineName: string): Promise<void> {
    if (!environment.production && environment.devMachineName) {
      // Quickly allow a dev to see what a certain site looks like
      //console.log('change from ', machineName, ' to ', environment.devMachineName);
      machineName = environment.devMachineName;
    }
    this.siteMachineName = machineName;
    const baseConfig = await firstValueFrom(
      this.httpClient.get(this.getCacheBustUrl(this.urlBase + '/assets/sites/base-site/config.json', 'base-site')),
    );
    const siteConfig = await firstValueFrom(
      this.httpClient.get(
        this.getCacheBustUrl(this.urlBase + '/assets/sites/' + machineName + '/config.json', machineName),
      ),
    );
    this.applyConfig(baseConfig, siteConfig);
    if (!environment.production && environment.devSkinMachineName) {
      // Quickly allow a dev to see what a certain site looks like
      //console.log('change skin to ', environment.devSkinMachineName);
      await this.setSkinConfig(environment.devSkinMachineName);
    }
  }

  public async setConfigByOrigin() {
    for (let i = 0, len = environment.siteList.length; i < len; i++) {
      const site = environment.siteList[i];
      if (site.link.indexOf(this.document.location.origin) > -1) {
        return this.setConfig(site.machineName);
        break;
      }
    }
  }

  public async setSkinConfig(skinName: string): Promise<void> {
    if (!environment.production && environment.devSkinMachineName) {
      // Quickly allow a dev to see what a certain site looks like
      //console.log('change skin to ', environment.devSkinMachineName);
      skinName = environment.devSkinMachineName;
    }
    const baseConfig = await firstValueFrom(
      this.httpClient.get(this.getCacheBustUrl(this.urlBase + '/assets/sites/base-site/config.json', 'base-site')),
    );
    const siteConfig = await firstValueFrom(
      this.httpClient.get(
        this.siteMachineName
          ? this.getCacheBustUrl(
              this.urlBase + '/assets/sites/' + this.siteMachineName + '/config.json',
              this.siteMachineName,
            )
          : this.getCacheBustUrl(this.urlBase + '/assets/sites/base-site/config.json', 'base-site'),
      ),
    );

    try {
      const isSkinNameNumber = !isNaN(Number(skinName));
      const skinConfig = isSkinNameNumber
        ? await firstValueFrom(
            this.httpClient.get(
              this.getCacheBustUrl(this.skinUrlBase + '/site-skins/' + skinName + '/config.json', skinName, 'skins'),
            ),
          )
        : await firstValueFrom(
            this.httpClient.get(
              this.getCacheBustUrl(this.urlBase + '/assets/skins/' + skinName + '/config.json', skinName, 'skins'),
            ),
          );

      this.applyConfig(baseConfig, siteConfig, skinConfig, isSkinNameNumber, skinName);
    } catch (e) {
      return Promise.resolve();
    }
  }

  public unsetSkin(): Promise<any> {
    return this.setConfig(this.siteMachineName);
  }

  protected getCacheBustUrl(url: string, machineName: string, type: 'sites' | 'skins' = 'sites') {
    const cacheValue = this.staticAssets['assets/' + type + '/' + machineName + '/config.json'];

    if (cacheValue) {
      return url + '?c=1' + cacheValue;
    }

    return url;
  }
}
